import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import apiClient from '../api/client';
import { useModulePermission } from '../utils/useModulePermission';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ProjectsPageProps {
  startDate: string | null;
  endDate: string | null;
}

interface ProjectData {
  project_name: string;
  geo: string;
  account_manager: string;
  month: string;
  revenue: number;
  employee_cost: number;
  sub_con_cost: number;
  other_cost: number;
  gross_margin: number;
  gross_margin_percentage: number;
  total_days_available: number;
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string;
    fill?: boolean;
    tension?: number;
    borderWidth?: number;
    borderDash?: number[];
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointBorderWidth?: number;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

interface ProjectEmployee {
  employee_id: string;
  employee_name: string;
  role?: string;
  status?: string;
  latest_month?: string;
  days_allocated?: number;
  available_days?: number;
  allocation_percentage?: number;
  actual_hours?: number;
  estimated_hours?: number;
  salary_inr?: number;
  salary_cost_usd?: number;
  salary_cost_inr?: number;
  hourly_rate?: number;
  months_allocated?: string[];
  total_days_allocated?: number;
  allocation_count?: number;
}

const PROJECTS_PAGE_FILTERS_KEY = 'ProjectsPage.filters';

function getStoredFilters(): { selectedGeo: string; selectedProject: string; gmTargetValue: number } {
  try {
    const raw = sessionStorage.getItem(PROJECTS_PAGE_FILTERS_KEY);
    if (!raw) return { selectedGeo: '', selectedProject: '', gmTargetValue: 60 };
    const parsed = JSON.parse(raw);
    const gm = typeof parsed.gmTargetValue === 'number' && Number.isFinite(parsed.gmTargetValue)
      ? Math.min(100, Math.max(0, parsed.gmTargetValue))
      : 60;
    return {
      selectedGeo: typeof parsed.selectedGeo === 'string' ? parsed.selectedGeo : '',
      selectedProject: typeof parsed.selectedProject === 'string' ? parsed.selectedProject : '',
      gmTargetValue: gm,
    };
  } catch {
    return { selectedGeo: '', selectedProject: '', gmTargetValue: 60 };
  }
}

const ProjectsPage: React.FC<ProjectsPageProps> = ({ startDate, endDate }) => {
  const { can } = useModulePermission("Projects");
  const [selectedGeo, setSelectedGeo] = useState<string>(() => getStoredFilters().selectedGeo);
  const [selectedProject, setSelectedProject] = useState<string>(() => getStoredFilters().selectedProject);
  const [gmTargetValue, setGmTargetValue] = useState<number>(() => getStoredFilters().gmTargetValue);
  const [comparisonStartDate, setComparisonStartDate] = useState<string>('');
  const [comparisonEndDate, setComparisonEndDate] = useState<string>('');
  const [geoOptions, setGeoOptions] = useState<string[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: string; label: string; geo: string }[]>([]);
  const [projectData, setProjectData] = useState<ProjectData[]>([]);
  const [, setComparisonData] = useState<ProjectData[]>([]);
  const [changeAnalysisData, setChangeAnalysisData] = useState<any[]>([]);
  const [employeeCostAnalysisData, setEmployeeCostAnalysisData] = useState<any>(null);
  const [costChartData, setCostChartData] = useState<ChartData | null>(null);
  const [gmChartData, setGmChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [projectEmployees, setProjectEmployees] = useState<ProjectEmployee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [employeesSummary, setEmployeesSummary] = useState<{ project_name?: string; total_employees?: number } | null>(null);

  // Persist filter values to sessionStorage when they change
  useEffect(() => {
    try {
      sessionStorage.setItem(
        PROJECTS_PAGE_FILTERS_KEY,
        JSON.stringify({
          selectedGeo,
          selectedProject,
          gmTargetValue,
        })
      );
    } catch {
      // ignore storage errors
    }
  }, [selectedGeo, selectedProject, gmTargetValue]);

  const monthToSortableKey = useCallback((m: string) => {
    const trimmed = (m ?? '').trim();
    if (!trimmed) return '';

    // Already sortable: YYYY-MM
    if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;

    // Handle Apr-25 / Apr 25 / Apr-2025
    const monYr = trimmed.match(/^([A-Za-z]{3})[-\s](\d{2}|\d{4})$/);
    if (monYr) {
      const mon = monYr[1];
      const yrRaw = monYr[2];
      const monthIndex = new Date(`${mon} 1, 2000`).getMonth() + 1; // 1-based
      if (Number.isNaN(monthIndex)) return trimmed;
      const year = yrRaw.length === 2 ? `20${yrRaw}` : yrRaw;
      return `${year}-${String(monthIndex).padStart(2, '0')}`;
    }

    return trimmed;
  }, []);

  const formatMonthDisplay = useCallback(
    (m: string) => {
      const sortable = monthToSortableKey(m);
      const match = sortable.match(/^(\d{4})-(\d{2})$/);
      if (!match) {
        // Normalize "Apr 25" -> "Apr-25" when possible
        const monYr = (m ?? '').trim().match(/^([A-Za-z]{3})\s(\d{2})$/);
        if (monYr) return `${monYr[1]}-${monYr[2]}`;
        return (m ?? '').trim();
      }

      const year = match[1];
      const monthNum = Number(match[2]);
      const date = new Date(Number(year), monthNum - 1, 1);
      const mon = date.toLocaleString('en-US', { month: 'short' });
      const yy = String(Number(year) % 100).padStart(2, '0');
      return `${mon}-${yy}`;
    },
    [monthToSortableKey]
  );

  // Fetch GEO options
  const fetchGeoOptions = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/delivery/geo-options');
      setGeoOptions(response.data.geo_options);
    } catch (err: any) {
    }
  }, []);

  // Fetch project options based on selected GEO
  const fetchProjectOptions = useCallback(async () => {
    try {
      if (selectedGeo) {
        const params = { geo: selectedGeo };
        const response = await apiClient.get('/api/v1/delivery/project-options', { params });
        setProjectOptions(response.data.projects);
      }
    } catch (err: any) {
    }
  }, [selectedGeo]);

  const formatDateForAPI = useCallback((dateStr: string | null) => {
    if (!dateStr) return '';
    if (dateStr.length === 7) return `${dateStr}-01`; // YYYY-MM -> YYYY-MM-01
    return dateStr;
  }, []);

  // Fetch employee list for selected project
  const fetchProjectEmployees = useCallback(async () => {
    if (!selectedProject) {
      setProjectEmployees([]);
      setEmployeesSummary(null);
      setEmployeesError(null);
      setEmployeesLoading(false);
      return;
    }
    if (!startDate || !endDate) return;

    setEmployeesLoading(true);
    setEmployeesError(null);
    try {
      const params = {
        project_name: selectedProject,
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };
      const res = await apiClient.get('/api/v1/delivery/project-employees', { params });
      const employees = Array.isArray(res.data?.employees) ? (res.data.employees as ProjectEmployee[]) : [];
      setEmployeesSummary({
        project_name: res.data?.project_name,
        total_employees: typeof res.data?.total_employees === 'number' ? res.data.total_employees : employees.length,
      });
      setProjectEmployees(employees);
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to load employees';
      setEmployeesError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setProjectEmployees([]);
      setEmployeesSummary(null);
    } finally {
      setEmployeesLoading(false);
    }
  }, [selectedProject, startDate, endDate, formatDateForAPI]);

  // Generate chart data from project data
  const generateChartData = useCallback((projects: ProjectData[]) => {
    // If no project is selected, show blank charts
    if (!selectedProject) {
      setCostChartData(null);
      setGmChartData(null);
      return;
    }

    if (projects.length === 0) {
      setCostChartData(null);
      setGmChartData(null);
      return;
    }

    // Show data for the selected project only
    const filteredProjectData = projects.filter(p => p.project_name === selectedProject);

    if (filteredProjectData.length === 0) {
      setCostChartData(null);
      setGmChartData(null);
      return;
    }

    // Sort data by month for proper chronological order
    const sortedData = filteredProjectData.sort((a, b) => {
      const aKey = monthToSortableKey(a.month || '');
      const bKey = monthToSortableKey(b.month || '');
      return aKey.localeCompare(bKey);
    });

    // Extract labels (months) and data arrays
    const labels = sortedData.map(item => formatMonthDisplay(item.month || ''));
    const revenueData = sortedData.map(item => item.revenue || 0);
    const employeeCostData = sortedData.map(item => item.employee_cost || 0);
    const subConCostData = sortedData.map(item => item.sub_con_cost || 0);
    const otherCostData = sortedData.map(item => item.other_cost || 0);
    const gmPercentageData = sortedData.map(item => item.gross_margin_percentage || 0);

    // Cost Heads Chart Data
    const costData: ChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Revenue ($)',
          data: revenueData,
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: false,
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: '#3B82F6',
          pointBorderColor: '#3B82F6',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Employee Cost ($)',
          data: employeeCostData,
          borderColor: '#64748B',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: false,
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: '#64748B',
          pointBorderColor: '#64748B',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Sub Con Cost ($)',
          data: subConCostData,
          borderColor: 'rgb(245, 158, 11)',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          fill: false,
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: 'rgb(245, 158, 11)',
          pointBorderColor: 'rgb(245, 158, 11)',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Other Cost ($)',
          data: otherCostData,
          borderColor: 'rgb(139, 69, 19)',
          backgroundColor: 'rgba(139, 69, 19, 0.1)',
          fill: false,
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: 'rgb(139, 69, 19)',
          pointBorderColor: 'rgb(139, 69, 19)',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };

    // GM% Chart Data
    const gmData: ChartData = {
      labels: labels,
      datasets: [
        {
          label: 'GM%',
          data: gmPercentageData,
          borderColor: '#10B981',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: false,
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#10B981',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        // Add target line
        {
          label: `Target GM% (${gmTargetValue}%)`,
          data: new Array(labels.length).fill(gmTargetValue),
          borderColor: '#09f5a6ff',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: false,
          tension: 0,
          borderWidth: 2,
          borderDash: [5, 5],
          pointBackgroundColor: '#09f5a6ff',
          pointBorderColor: '#09f5a6ff',
          pointBorderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
      ],
    };

    setCostChartData(costData);
    setGmChartData(gmData);
  }, [formatMonthDisplay, gmTargetValue, monthToSortableKey, selectedProject]);

  // Fetch project financial data
  const fetchProjectData = useCallback(async () => {
    if (!startDate || !endDate) {
      setCostChartData(null);
      setGmChartData(null);
      return;
    }

      setLoading(true);
    setError(null);

    try {
      // Convert YYYY-MM format to YYYY-MM-DD format
      const formatDateForAPI = (dateStr: string) => {
        if (dateStr && dateStr.length === 7) { // YYYY-MM format
          return `${dateStr}-01`; // Add -01 for first day of month
        }
        return dateStr;
      };

      const params: any = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };

      if (selectedGeo) {
        params.geo = selectedGeo;
      }

      if (selectedProject) {
        params.project_name = selectedProject;
      }

      const response = await apiClient.get('/api/v1/delivery/project-financial-data', { params });
      
      // Handle different response formats - check for projects array in various locations
      const projects = response.data.projects || response.data.data?.projects || response.data.data || response.data || [];
      
      setProjectData(Array.isArray(projects) ? projects : []);

      // Generate chart data
      generateChartData(Array.isArray(projects) ? projects : []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch project data';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      } finally {
        setLoading(false);
      }
  }, [startDate, endDate, selectedGeo, selectedProject, generateChartData]);

  // Fetch comparison data for change analysis
  const fetchComparisonData = useCallback(async () => {
    if (!comparisonStartDate || !comparisonEndDate || !selectedGeo) {
      setComparisonData([]);
      return;
    }

    try {
      // Convert YYYY-MM format to YYYY-MM-DD format
      const formatDateForAPI = (dateStr: string) => {
        if (dateStr && dateStr.length === 7) { // YYYY-MM format
          return `${dateStr}-01`; // Add -01 for first day of month
        }
        return dateStr;
      };

      const params: any = {
        start_date: formatDateForAPI(comparisonStartDate),
        end_date: formatDateForAPI(comparisonEndDate),
        geo: selectedGeo,
      };

      const response = await apiClient.get('/api/v1/delivery/project-financial-data', { params });
      setComparisonData(response.data.projects || []);
    } catch (err: any) {
      setComparisonData([]);
    }
  }, [comparisonStartDate, comparisonEndDate, selectedGeo]);

  // Generate table data for GM% by project and month
  const generateTableData = (projects: ProjectData[]) => {
    if (!selectedGeo || projects.length === 0) {
      return { projects: [], months: [] };
    }

    // Filter projects by selected GEO
    const geoProjects = projects.filter(p => p.geo === selectedGeo);
    
    // Get unique months and sort them chronologically
    const months = Array.from(new Set(geoProjects.map(p => p.month))).sort((a, b) => {
      return monthToSortableKey(a).localeCompare(monthToSortableKey(b));
    });

    // Get unique projects
    const uniqueProjects = Array.from(new Set(geoProjects.map(p => p.project_name)));

    // Create table data structure
    const tableData = uniqueProjects.map(projectName => {
      const projectData: { [key: string]: number | string } = { project: projectName };
      
      months.forEach(month => {
        const monthData = geoProjects.find(p => p.project_name === projectName && p.month === month);
        projectData[month] = monthData ? monthData.gross_margin_percentage : 0;
      });
      
      return projectData;
    });

    return { projects: tableData, months };
  };

  // Generate available months for comparison date selection
  const getAvailableMonths = () => {
    if (!startDate || !endDate) return [];
    
    const months: { value: string; label: string }[] = [];
    const start = new Date(startDate + '-01');
    const end = new Date(endDate + '-01');
    
    const current = new Date(start);
    while (current <= end) {
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const value = `${year}-${month}`;
      const label = formatMonthDisplay(value);
      
      months.push({ value, label });
      
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  // Fetch and update change analysis data
  const fetchChangeAnalysis = useCallback(async () => {
    if (!selectedGeo || !comparisonStartDate || !comparisonEndDate) {
      setChangeAnalysisData([]);
      return;
    }

    try {
      // Fetch data for both comparison periods independently
      const fetchComparisonPeriodData = async (startDate: string, endDate: string) => {
        const formatDateForAPI = (dateStr: string) => {
          if (dateStr && dateStr.length === 7) { // YYYY-MM format
            return `${dateStr}-01`; // Add -01 for first day of month
          }
          return dateStr;
        };

        const params = {
          start_date: formatDateForAPI(startDate),
          end_date: formatDateForAPI(endDate),
          geo: selectedGeo,
        };

        const response = await apiClient.get('/api/v1/delivery/project-financial-data', { params });
        return response.data.projects || [];
      };

      const period1Data = await fetchComparisonPeriodData(comparisonStartDate, comparisonStartDate);
      const period2Data = await fetchComparisonPeriodData(comparisonEndDate, comparisonEndDate);

      // Get unique projects from both periods
      const period1Projects = Array.from(new Set(period1Data.map((p: ProjectData) => p.project_name)));
      const period2Projects = Array.from(new Set(period2Data.map((p: ProjectData) => p.project_name)));
      const allProjects = Array.from(new Set([...period1Projects, ...period2Projects]));

      const analysisResults = allProjects.map(projectName => {
        // Get data for this project from both periods
        const period1Project = period1Data.find((p: ProjectData) => p.project_name === projectName) || {};
        const period2Project = period2Data.find((p: ProjectData) => p.project_name === projectName) || {};

        // Get values from each period
        const period1Values = {
          revenue: period1Project.revenue || 0,
          employee_cost: period1Project.employee_cost || 0,
          sub_con_cost: period1Project.sub_con_cost || 0,
          other_cost: period1Project.other_cost || 0,
          gross_margin: period1Project.gross_margin || 0,
        };

        const period2Values = {
          revenue: period2Project.revenue || 0,
          employee_cost: period2Project.employee_cost || 0,
          sub_con_cost: period2Project.sub_con_cost || 0,
          other_cost: period2Project.other_cost || 0,
          gross_margin: period2Project.gross_margin || 0,
        };

        // Calculate GM% for each period
        const period1TotalCosts = period1Values.employee_cost + period1Values.sub_con_cost + period1Values.other_cost;
        const period1GM = period1Values.revenue > 0 ? ((period1Values.revenue - period1TotalCosts) / period1Values.revenue) * 100 : 0;

        const period2TotalCosts = period2Values.employee_cost + period2Values.sub_con_cost + period2Values.other_cost;
        const period2GM = period2Values.revenue > 0 ? ((period2Values.revenue - period2TotalCosts) / period2Values.revenue) * 100 : 0;

        // Calculate changes (Period 2 - Period 1)
        const revenueChange = period2Values.revenue - period1Values.revenue;
        const employeeCostChange = period2Values.employee_cost - period1Values.employee_cost;
        const subConCostChange = period2Values.sub_con_cost - period1Values.sub_con_cost;
        const otherCostChange = period2Values.other_cost - period1Values.other_cost;
        const grossMarginChange = period2Values.gross_margin - period1Values.gross_margin;
        const gmPercentageChange = period2GM - period1GM;

        return {
          project: projectName,
          current: period2Values, // Period 2 is "current"
          comparison: period1Values, // Period 1 is "comparison"
          changes: {
            revenue: revenueChange,
            employee_cost: employeeCostChange,
            sub_con_cost: subConCostChange,
            other_cost: otherCostChange,
            gross_margin: grossMarginChange,
            gross_margin_percentage: gmPercentageChange,
          }
        };
      });

      setChangeAnalysisData(analysisResults);
    } catch (err) {
      setChangeAnalysisData([]);
    }
  }, [selectedGeo, comparisonStartDate, comparisonEndDate]);

  // Fetch detailed employee cost analysis for selected project
  const fetchEmployeeCostAnalysis = useCallback(async () => {
    if (!selectedProject || !comparisonStartDate || !comparisonEndDate) {
      setEmployeeCostAnalysisData(null);
      return;
    }

    try {
      const formatDateForAPI = (dateStr: string) => {
        if (dateStr && dateStr.length === 7) { // YYYY-MM format
          return `${dateStr}-01`; // Add -01 for first day of month
        }
        return dateStr;
      };

      const params = {
        project_name: selectedProject,
        comparison_start_date: formatDateForAPI(comparisonStartDate),
        comparison_end_date: formatDateForAPI(comparisonEndDate),
      };

      const response = await apiClient.get('/api/v1/delivery/project-employee-cost-analysis', { params });
      setEmployeeCostAnalysisData(response.data);
    } catch (err) {
      setEmployeeCostAnalysisData(null);
    }
  }, [selectedProject, comparisonStartDate, comparisonEndDate]);

  // Load initial data
  useEffect(() => {
    fetchGeoOptions();
    // Reset charts when component mounts
    setCostChartData(null);
    setGmChartData(null);
  }, [fetchGeoOptions]);

  // Default-select first GEO when options load (do not override user choice)
  useEffect(() => {
    if (selectedGeo) return;
    if (!geoOptions || geoOptions.length === 0) return;
    const firstGeo = geoOptions.find((g) => Boolean(g)) ?? geoOptions[0];
    if (firstGeo) setSelectedGeo(firstGeo);
  }, [geoOptions, selectedGeo]);

  // Update project options when GEO changes
  useEffect(() => {
    fetchProjectOptions();
    setSelectedProject(''); // Reset project selection when GEO changes
    // Reset charts when GEO changes
    setCostChartData(null);
    setGmChartData(null);
  }, [fetchProjectOptions]);

  // Default-select first Project when options load (do not override user choice)
  useEffect(() => {
    if (selectedProject) return;
    if (!projectOptions || projectOptions.length === 0) return;
    const firstProjectValue = projectOptions[0]?.value;
    if (firstProjectValue) setSelectedProject(firstProjectValue);
  }, [projectOptions, selectedProject]);

  // Fetch data when filters or date range changes
  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Fetch employees when project or date range changes
  useEffect(() => {
    fetchProjectEmployees();
  }, [fetchProjectEmployees]);

  // Regenerate charts when GM target value changes
  useEffect(() => {
    if (projectData.length > 0) {
      generateChartData(projectData);
    }
  }, [projectData, generateChartData]);

  // Fetch comparison data when comparison dates change
  useEffect(() => {
    fetchComparisonData();
  }, [fetchComparisonData]);

  // Fetch change analysis when comparison dates change
  useEffect(() => {
    fetchChangeAnalysis();
  }, [fetchChangeAnalysis]);

  // Fetch employee cost analysis when project or comparison dates change
  useEffect(() => {
    fetchEmployeeCostAnalysis();
  }, [fetchEmployeeCostAnalysis]);

  // Chart options for Cost Heads
  const costChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    hover: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: 'Revenue & Cost Components ($)',
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            return `${context.dataset.label}: $${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Month',
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount ($)',
        },
        ticks: {
          callback: function(value: any) {
            return '$' + value.toLocaleString();
          }
        }
      },
    },
  };

  // Chart options for GM%
  const gmChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    hover: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: 'Gross Margin %',
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value.toFixed(1)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Month',
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Percentage (%)',
        },
        ticks: {
          callback: function(value: any) {
            return value.toFixed(1) + '%';
          }
        }
      },
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* <Typography variant="h4" gutterBottom>
        Projects Analysis
      </Typography> */}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
        {/* GEO */}
        <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>GEO</InputLabel>
                <Select
                  value={selectedGeo}
                  label="GEO"
                  onChange={(e) => setSelectedGeo(e.target.value)}
                >
                  {geoOptions.map((geo) => (
                    <MenuItem key={geo} value={geo}>
                      {geo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
        </Grid>
        {/* Project */}
        <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProject}
                  label="Project"
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={projectOptions.length === 0}
                >
                  {projectOptions.map((project) => (
                    <MenuItem key={project.value} value={project.value}>
                      {project.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
        </Grid>
        {/* GM Target */}
        <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="GM Target (%)"
                type="number"
                value={gmTargetValue}
                onChange={(e) => setGmTargetValue(Number(e.target.value))}
                inputProps={{ min: 0, max: 100, step: 1 }}
          />
        </Grid>
      </Grid>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data loaded info */}
      {/* {!loading && !error && projectData.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loaded {projectData.length} project records. 
          {!selectedGeo && ' Select a GEO to view the GM% table.'}
          {!selectedProject && ' Select a Project to view the charts.'}
        </Alert>
      )} */}

      {/* No data warning */}
      {/* {!loading && !error && projectData.length === 0 && startDate && endDate && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No project data found for the selected date range.
        </Alert>
      )} */}

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Cost Heads Chart */}
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {selectedProject 
                  ? `Revenue & Cost Components: ${selectedProject}` 
                  : 'Revenue & Cost Components'
                }
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : costChartData ? (
                <Box sx={{ height: 400 }}>
                  <Line data={costChartData} options={costChartOptions} />
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No cost data available for the selected filters and date range.
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* GM% Chart */}
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {selectedProject 
                  ? `GM%: ${selectedProject}` 
                  : 'GM%'
                }
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : gmChartData ? (
                <Box sx={{ height: 400 }}>
                  <Line data={gmChartData} options={gmChartOptions} />
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No GM% data available for the selected filters and date range.
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* GM% Table */}
      {selectedGeo && projectData.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              GM% by Project and Month - {selectedGeo}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Project
                    </TableCell>
                    {generateTableData(projectData).months.map((month) => (
                      <TableCell 
                        key={month} 
                        align="center" 
                        sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}
                      >
                        {formatMonthDisplay(month)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {generateTableData(projectData).projects.map((project, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {project.project}
                      </TableCell>
                      {generateTableData(projectData).months.map((month) => (
                        <TableCell key={month} align="center">
                          {typeof project[month] === 'number' 
                            ? `${(project[month] as number).toFixed(1)}%`
                            : '0.0%'
                          }
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

           {/* Employee List (selected project) */}
      {selectedProject && startDate && endDate && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Employees - {selectedProject}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {employeesSummary?.total_employees ?? projectEmployees.length} employee(s)
              </Typography>
            </Box>

            {employeesError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {employeesError}
              </Alert>
            ) : null}

            {employeesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={26} />
              </Box>
            ) : projectEmployees.length === 0 ? (
              <Paper variant="outlined" sx={{ mt: 2, p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No employees found for the selected project and date range.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1100 }}>
                  <TableHead sx={{ backgroundColor: 'grey.100' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Employee</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Latest Month</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Days Alloc.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Avail. Days</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Alloc. %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Salary (₹)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Cost (₹)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Cost ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectEmployees.map((e, idx) => (
                      <TableRow key={`${e.employee_id}_${idx}`} hover>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                            {e.employee_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            ID: {e.employee_id}
                          </Typography>
                        </TableCell>
                        <TableCell>{e.role || '—'}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{e.status || '—'}</TableCell>
                        <TableCell>{e.latest_month || '—'}</TableCell>
                        <TableCell align="right">{typeof e.days_allocated === 'number' ? e.days_allocated.toFixed(1) : '—'}</TableCell>
                        <TableCell align="right">{typeof e.available_days === 'number' ? e.available_days.toFixed(1) : '—'}</TableCell>
                        <TableCell align="right">
                          {typeof e.allocation_percentage === 'number' ? `${e.allocation_percentage.toFixed(1)}%` : '—'}
                        </TableCell>
                        <TableCell align="right">{typeof e.salary_inr === 'number' ? e.salary_inr.toLocaleString() : '—'}</TableCell>
                        <TableCell align="right">{typeof e.salary_cost_inr === 'number' ? e.salary_cost_inr.toLocaleString() : '—'}</TableCell>
                        <TableCell align="right">{typeof e.salary_cost_usd === 'number' ? e.salary_cost_usd.toLocaleString() : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Change Analysis Section */}
      {selectedGeo && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Change Analysis - {selectedGeo}
            </Typography>
            
            {/* Comparison Period Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Comparison Start Date</InputLabel>
                  <Select
                    value={comparisonStartDate}
                    label="Comparison Start Date"
                    onChange={(e) => setComparisonStartDate(e.target.value)}
                  >
                    {getAvailableMonths().map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Comparison End Date</InputLabel>
                  <Select
                    value={comparisonEndDate}
                    label="Comparison End Date"
                    onChange={(e) => setComparisonEndDate(e.target.value)}
                  >
                    {getAvailableMonths().map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Change Analysis Table */}
            {/* {comparisonStartDate && comparisonEndDate && changeAnalysisData.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Project
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Revenue Change ($)
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Employee Cost Change ($)
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Sub Con Cost Change ($)
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Other Cost Change ($)
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Gross Margin Change ($)
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        GM% Change (pp)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {changeAnalysisData.map((analysis, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 'medium' }}>
                          {analysis.project}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          color: analysis.changes.revenue >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}>
                          {analysis.changes.revenue >= 0 ? '+' : ''}${analysis.changes.revenue.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          color: analysis.changes.employee_cost >= 0 ? 'error.main' : 'success.main',
                          fontWeight: 'medium'
                        }}>
                          {analysis.changes.employee_cost >= 0 ? '+' : ''}${analysis.changes.employee_cost.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          color: analysis.changes.sub_con_cost >= 0 ? 'error.main' : 'success.main',
                          fontWeight: 'medium'
                        }}>
                          {analysis.changes.sub_con_cost >= 0 ? '+' : ''}${analysis.changes.sub_con_cost.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          color: analysis.changes.other_cost >= 0 ? 'error.main' : 'success.main',
                          fontWeight: 'medium'
                        }}>
                          {analysis.changes.other_cost >= 0 ? '+' : ''}${analysis.changes.other_cost.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          color: analysis.changes.gross_margin >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}>
                          {analysis.changes.gross_margin >= 0 ? '+' : ''}${analysis.changes.gross_margin.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          color: analysis.changes.gross_margin_percentage >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}>
                          {analysis.changes.gross_margin_percentage >= 0 ? '+' : ''}{analysis.changes.gross_margin_percentage.toFixed(1)} pp
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {comparisonStartDate && comparisonEndDate && changeAnalysisData.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No data available for the selected comparison period.
                </Typography>
              </Paper>
            )} */}
          </CardContent>
        </Card>
      )}


 

      {/* Employee Cost Analysis Section */}
      {selectedProject && comparisonStartDate && comparisonEndDate && employeeCostAnalysisData && (() => {
        // Helper function to format period (e.g., "2025-08" -> "Aug 25" or "Aug-25" -> "Aug 25")
        const formatPeriod = (period: string): string => {
          if (!period) return '';
          
          // If already in "MMM-YY" format, convert to "MMM YY"
          if (period.includes('-') && period.length <= 7) {
            const [month, year] = period.split('-');
            return `${month} ${year}`;
          }
          
          // If in "YYYY-MM" format, convert to "MMM YY"
          if (period.includes('-') && period.length === 7 && period.startsWith('20')) {
            const [year, month] = period.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1);
            return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          }
          
          return period;
        };

        const startPeriodFormatted = formatPeriod(employeeCostAnalysisData.comparison_periods.start_period);
        const endPeriodFormatted = formatPeriod(employeeCostAnalysisData.comparison_periods.end_period);

        return (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Employee Cost Analysis - {selectedProject}
            </Typography>
            
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" color="primary">
                    ${employeeCostAnalysisData.summary.total_cost_change.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Cost Change
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'success.light' }}>
                  <Typography variant="h6" color="success.dark">
                    {employeeCostAnalysisData.summary.employees_with_increased_cost}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cost Increased
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'error.light' }}>
                  <Typography variant="h6" color="error.dark">
                    {employeeCostAnalysisData.summary.employees_with_decreased_cost}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cost Decreased
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'info.light' }}>
                  <Typography variant="h6" color="info.dark">
                    {employeeCostAnalysisData.summary.new_employees}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New to Project
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Employee Details Table */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Employee Cost Breakdown ({employeeCostAnalysisData.comparison_periods.start_period} vs {employeeCostAnalysisData.comparison_periods.end_period})
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              💡 Scroll horizontally to view all columns. Employee names remain visible while scrolling.
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1400 }}>
                <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.100', zIndex: 1 }}>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: 'grey.100',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid #e0e0e0'
                    }}>
                      Employee
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Role
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      {startPeriodFormatted} Days Allocated
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      {endPeriodFormatted} Days Allocated
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Days Change
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      {startPeriodFormatted} Available Days
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      {endPeriodFormatted} Available Days
                    </TableCell>
                     <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                       {startPeriodFormatted} Allocation %
                     </TableCell>
                     <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                       {endPeriodFormatted} Allocation %
                     </TableCell>
                     <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                       Allocation % Change
                     </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      {startPeriodFormatted} Salary Cost (INR)
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      {endPeriodFormatted} Salary Cost (INR)
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Salary INR Change
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Cost Change (INR)
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Cost Change (%)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Summary
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeCostAnalysisData.employee_analysis.map((employee: any, index: number) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ 
                        fontWeight: 'medium',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                        borderRight: '1px solid #e0e0e0'
                      }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {employee.employee_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {employee.employee_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {employee.role}
                      </TableCell>
                      {/* Start Days Allocated */}
                      <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                        {employee.changes.start_days_allocated.toFixed(1)}
                      </TableCell>
                      {/* End Days Allocated */}
                      <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                        {employee.changes.end_days_allocated.toFixed(1)}
                      </TableCell>
                      {/* Days Change */}
                      <TableCell align="center" sx={{ 
                        color: employee.changes.days_allocated_change >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 'medium'
                      }}>
                        {employee.changes.days_allocated_change >= 0 ? '+' : ''}{employee.changes.days_allocated_change.toFixed(1)}
                      </TableCell>
                      {/* Start Available Days */}
                      <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                        {employee.changes.start_available_days.toFixed(1)}
                      </TableCell>
                      {/* End Available Days */}
                      <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                        {employee.changes.end_available_days.toFixed(1)}
                      </TableCell>
                      {/* Start Allocation % */}
                      <TableCell align="center" sx={{ 
                        fontWeight: 'medium',
                        color: 'text.primary'
                      }}>
                        {employee.changes.start_allocation_percentage.toFixed(1)}%
                      </TableCell>
                      {/* End Allocation % */}
                      <TableCell align="center" sx={{ 
                        fontWeight: 'medium',
                        color: 'text.primary'
                      }}>
                        {employee.changes.end_allocation_percentage.toFixed(1)}%
                      </TableCell>
                      {/* Allocation % Change */}
                      <TableCell align="center" sx={{ 
                        color: employee.changes.allocation_percentage_change >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 'medium'
                      }}>
                        {employee.changes.allocation_percentage_change >= 0 ? '+' : ''}{employee.changes.allocation_percentage_change.toFixed(1)}%
                      </TableCell>
                      {/* Start Salary Cost (INR) */}
                      <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                        ₹{employee.changes.start_salary_cost_inr.toLocaleString()}
                      </TableCell>
                      {/* End Salary Cost (INR) */}
                      <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                        ₹{employee.changes.end_salary_cost_inr.toLocaleString()}
                      </TableCell>
                      {/* Salary INR Change */}
                      <TableCell align="center" sx={{ 
                        color: employee.is_new_to_project ? 'info.main' : (employee.changes.salary_inr_change >= 0 ? 'error.main' : 'success.main'),
                        fontWeight: 'medium'
                      }}>
                        {employee.is_new_to_project ? `₹${employee.changes.salary_inr_change.toLocaleString()}` : `${employee.changes.salary_inr_change >= 0 ? '+' : ''}₹${employee.changes.salary_inr_change.toLocaleString()}`}
                      </TableCell>
                      {/* Cost Change (INR) */}
                      <TableCell align="center" sx={{ 
                        color: employee.changes.cost_change_inr >= 0 ? 'error.main' : 'success.main',
                        fontWeight: 'medium'
                      }}>
                        {employee.changes.cost_change_inr >= 0 ? '+' : ''}₹{employee.changes.cost_change_inr.toLocaleString()}
                      </TableCell>
                      {/* Cost Change (%) */}
                      <TableCell align="center" sx={{ 
                        color: employee.is_new_to_project ? 'info.main' : (employee.changes.salary_cost_percentage_change >= 0 ? 'error.main' : 'success.main'),
                        fontWeight: 'medium'
                      }}>
                        {employee.is_new_to_project ? 'N/A' : `${employee.changes.salary_cost_percentage_change >= 0 ? '+' : ''}${employee.changes.salary_cost_percentage_change.toFixed(1)}%`}
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'medium',
                        fontSize: '0.875rem',
                        color: employee.is_new_to_project ? 'info.main' : 'text.primary'
                      }}>
                        {employee.summary}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {employeeCostAnalysisData.employee_analysis.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  No employee allocation data available for the selected project and comparison period.
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
        );
      })()}

    </Box>
  );
};

export default ProjectsPage;